import React, { Component, ReactNode } from 'react';
import './DocumentsView.scss';
import { DocumentsTable } from '../documents-table/DocumentsTable';
import { Document, EDocumentType } from '../../models/Document';
import { FileUpload } from '../file-upload/FileUpload';
import { UserRelationRoleEnum, DocumentResponse } from '../../api/types';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import { Api } from '../../api';
import { DossierMember, DossierMemberInterface } from '../../models/DossierMember';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { IDossierV2 } from '../../models/DossierV2';

interface DocumentsViewProps {
  member: DossierMember;
  dossier?: IDossierV2;
  dossierRelation?: DossierMemberInterface;
}

interface DocumentsViewState {
  documents: Document[];
  loading: boolean;
  loadedMember?: DossierMember;
  loadingError?: string;
}

class DocumentsView extends Component<DocumentsViewProps, DocumentsViewState> {
  state: DocumentsViewState = {
    documents: [],
    loading: true,
  };

  componentDidMount(): void {
    this.loadDocuments();
  }

  componentDidUpdate(): void {
    const { member } = this.props;
    const { loadedMember } = this.state;

    if (loadedMember && member.memberId !== loadedMember.memberId) {
      this.loadDocuments();
    }
  }

  loadDocuments(): void {
    this.setState({
      documents: [],
      loading: true,
      loadedMember: this.props.member,
      loadingError: undefined,
    });
    const { dossier } = this.props;
    const { memberId } = this.props.member;
    if (!dossier) {
      this.setState({ loadingError: 'Ophalen van materialen mislukt, er is geen dossier geselecteerd' });
      return;
    }
    Api.listResourceDocuments(dossier.id, memberId)
      .then(response => this.setState({ documents: Document.fromList(response) }))
      .catch(err => {
        console.error('Loading documents resources failed: ', err);
        this.setState({ loadingError: 'Ophalen van materialen mislukt, probeer het later opnieuw' });
      })
      .finally(() => this.setState({ loading: false }));
  }

  handleDownloadDocument = (document: Document): Promise<Blob> => {
    const { dossier } = this.props;
    const { memberId } = this.props.member;
    if (!dossier) {
      NotificationHelper.toastError('Openen bestand mislukt, er is geen dossier geselecteerd.');
      return Promise.reject(new Error('Dossier not set'));
    }
    return Api.getResourceDocument(dossier.id, memberId, document)
      .catch(err => {
        NotificationHelper.toastError('Openen bestand mislukt, probeer het later opnieuw.');
        throw err;
      });
  };

  handleDeleteDocument = (deleteDocument: Document): Promise<void> => {
    const { dossier } = this.props;
    const { memberId } = this.props.member;
    if (!dossier) {
      NotificationHelper.toastError('Verwijderen bestand mislukt, er is geen dossier geselecteerd.');
      return Promise.reject(new Error('Dossier not set'));
    }
    return Api.deleteResourceDocument(dossier.id, memberId, deleteDocument)
      .then(() => {
        const newDocumentsList: Document[] = [];
        this.state.documents.forEach(document => {
          if (!document.match(deleteDocument)) {
            newDocumentsList.push(document);
          }
        });
        this.setState({ documents: newDocumentsList });
      });
  };

  handleUpload = (formData: FormData): Promise<DocumentResponse> => {
    const { dossier } = this.props;
    const { memberId } = this.props.member;
    if (!dossier) {
      NotificationHelper.toastError('Uploaden bestand mislukt, er is geen dossier geselecteerd.');
      return Promise.reject(new Error('Dossier not set'));
    }
    return Api.uploadResourceDocument(dossier.id, memberId, formData)
      .catch(err => {
        if (err.key === 'InvalidFileTypeError') {
          NotificationHelper.toastError('Uploaden mislukt: Bestandstype niet toegestaan.');
        } else {
          NotificationHelper.toastError('Uploaden mislukt door een onverwachte fout.');
        }
        throw err;
      });
  };

  handleUploaded = (): void => this.loadDocuments();

  isDeleteAllowed(): boolean {
    const { dossier } = this.props;
    const { dossierRelation } = this.props;
    return !dossier?.archived && dossierRelation?.role === UserRelationRoleEnum.caregiver;
  }

  render(): ReactNode {
    const { dossier } = this.props;
    const { documents, loading, loadingError } = this.state;
    const isEmpty: boolean = (!loading && !loadingError && documents.length === 0);
    const allowDelete = this.isDeleteAllowed();
    return (
      <div className="documents-view__container">
        <div className="documents-view__items">
          { loadingError && <Alert variant="danger">{loadingError}</Alert> }
          { isEmpty && <Alert className="documents-view__info" variant="info">Er zijn nog geen materialen beschikbaar</Alert> }
          {
            loading ?
              <Spinner animation="border" variant="primary" /> :
              <>
                { !isEmpty && <DocumentsTable documents={documents} documentsType={EDocumentType.RESOURCE} memberId={this.props.member.memberId} allowDelete={allowDelete} onDeleteDocument={this.handleDeleteDocument} onDownloadDocument={this.handleDownloadDocument} /> }
                { !dossier?.archived && <FileUpload className="documents-view__file-upload" onUpload={this.handleUpload} onUploaded={this.handleUploaded} /> }
              </>
          }
        </div>
      </div>
    );
  }
}

export default connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  dossierRelation: state.session.relation,
}))(DocumentsView);
